import { useApolloClient, useLazyQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { TruncateTypography } from "components/TruncateTypography";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { DeepPartial } from "encamp-shared/src/utils/types";
import { gql } from "generated-graphql";
import {
  WasteLine,
  WasteLinesQuery,
  WasteManifestsQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import invariant from "invariant";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ExpandableCell } from "./ExpandableCell";
import { StaffManifestActionsMenu } from "./StaffManifestActionsMenu";

// Using this fragment to ensure we get cache replacement for the waste lines
gql(/* GraphQL */ `
  fragment WasteLineFragment on WasteLine {
    quantity
    manifestId
    qtyUnitOfMeasureDesc
    dotHazardous
    dotPrintedInformation
    nonHazWasteDescription
    vendorProfileDescription
    vendorProfileNumber
    managementMethodCode
    dotIdNumberDescription
    federalWasteCodes
    quantity
    qtyUnitOfMeasureCode
    qtyUnitOfMeasureDesc
    quantityTons
    quantityAcuteTons
    quantityNonAcuteTons
    quantityKg
    quantityAcuteKg
    quantityNonAcuteKg
  }
`);

const MANIFESTS_QUERY = gql(/* GraphQL */ `
  query WasteManifests(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    wasteManifests(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      items {
        id
        displayedManifestId
        epaId
        manifestTrackingNumber
        vendor
        shippedDate
        facilityName
        hasEManifest
        hasVendorManifest
        wasteLines {
          count
          items {
            ...WasteLineFragment
          }
        }
      }
      count
    }
  }
`);

const WASTE_LINES_QUERY = gql(/* GraphQL */ `
  query WasteLines($wasteManifestId: String!) {
    wasteManifestLineItems(wasteManifestId: $wasteManifestId) {
      count
      items {
        ...WasteLineFragment
      }
    }
  }
`);

export type Row = WasteManifestsQuery["wasteManifests"]["items"][0];

const replaceWasteLinesInCache = (
  cache: ReturnType<typeof useApolloClient>["cache"],
  wasteLinesResult: WasteLinesQuery["wasteManifestLineItems"],
  processedManifestId: string | null | undefined
) => {
  const cacheId = cache.identify({
    __typename: "ProcessedManifestSummary",
    id: processedManifestId,
  });
  cache.modify({
    broadcast: true,
    optimistic: true,
    id: cacheId,
    fields: {
      wasteLines() {
        return wasteLinesResult;
      },
    },
  });
};

export function Manifests() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { isStaff } = useCurrentUser();
  const persistenceKey = "manifests-table";
  const minWidth = 120;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  invariant(tenantId, "tenantId not found");

  const reportingYear = useSelectedReportingYear();

  const filename = useMemo(
    () => `waste‑Manifests-${reportingYear}`,
    [reportingYear]
  );

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} reportingYear:${reportingYear}`,
    [tenantId, reportingYear]
  );

  const client = useApolloClient();
  const [fetchWasteLines, { loading: loadingWasteLines }] =
    useLazyQuery(WASTE_LINES_QUERY);

  const handleFetchWasteLines = useCallback(
    (
      wasteManifestId: string | null | undefined,
      processedManifestId: string | null | undefined
    ) => {
      if (!wasteManifestId) {
        return;
      }

      fetchWasteLines({
        variables: {
          wasteManifestId,
        },
        onCompleted: (data) => {
          if (data?.wasteManifestLineItems) {
            replaceWasteLinesInCache(
              client.cache,
              data.wasteManifestLineItems,
              processedManifestId
            );
          }
        },
      });
    },
    [client.cache, fetchWasteLines]
  );
  const getButtons = useCallback(
    ({ search }: { search: string }) => {
      const btns = [
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            label: "Shipped Year",
            size: "small",
          }}
        />,
        <ExportDataButton
          key="export"
          fileName={filename}
          tenantId={tenantId}
          filter={{ search }}
          exportType="wasteManifests"
          dialog={true}
        />,
      ];
      if (isStaff) {
        btns.push(<StaffManifestActionsMenu key="staff" />);
      }
      return btns;
    },
    [filename, tenantId, isStaff]
  );

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifests",
    },
  ]);

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "epaId",
      headerName: "EPA ID",
      minWidth,
      flex: 1.2,
      topAligned: true,
      valueGetter: (_, row) => row?.epaId ?? "",
    },
    {
      field: "facility",
      headerName: "Facility",
      filterKeyType: "wasteFacility",
      minWidth,
      flex: 1.3,
      topAligned: true,
      valueGetter: (_, row) => row?.facilityName ?? "",
    },
    {
      field: "manifestTrackingNumber",
      headerName: "Manifest #",
      minWidth,
      flex: 1.2,
      topAligned: true,
      valueGetter: (_, row) => row.manifestTrackingNumber,
    },
    {
      field: "shippedDate",
      headerName: "Ship Date",
      width: 120,
      minWidth,
      topAligned: true,
      filterKeyType: "date",
      valueGetter: (_, row) => {
        return getUtcDisplayDate(row?.shippedDate, "med");
      },
    },
    {
      field: "wasteLines",
      headerName: "Line Item",
      flex: 1.5,
      minWidth,
      sortable: false,
      topAligned: true,
      renderCell: (cell) => {
        return (
          <Box height={"100%"}>
            <ExpandableCell
              items={cell.row.wasteLines?.items ?? []}
              totalCount={cell.row.wasteLines?.count}
              rowId={cell.id}
              clickDisabled={loadingWasteLines}
              onClick={() => {
                handleFetchWasteLines(
                  cell.row.displayedManifestId,
                  cell.row.id
                );
              }}
              collapsedLines={5}
              render={(lines) => {
                return (
                  <>
                    {lines?.map((line, idx) => (
                      <TruncateTypography key={idx} showTooltip variant="body">
                        {determineLineDisplay(line)}
                      </TruncateTypography>
                    ))}
                  </>
                );
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.7,
      minWidth: 80,
      sortable: false,
      topAligned: true,
      renderCell: (cell) => {
        return (
          <Box height={"100%"}>
            <ExpandableCell
              items={cell.row.wasteLines?.items ?? []}
              totalCount={cell.row.wasteLines?.count}
              rowId={cell.id}
              clickDisabled={loadingWasteLines}
              viewMoreDisabled
              onClick={() => {
                handleFetchWasteLines(
                  cell.row.displayedManifestId,
                  cell.row.id
                );
              }}
              collapsedLines={5}
              render={(lines) => {
                return (
                  <>
                    {lines?.map((line, idx) => (
                      <TruncateTypography key={idx} showTooltip variant="body">
                        {determineAmount(line)}
                      </TruncateTypography>
                    ))}
                  </>
                );
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "wasteProfileNumber",
      headerName: "Waste Profile #",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      topAligned: true,
      renderCell: (cell) => {
        return (
          <Box height={"100%"}>
            <ExpandableCell
              items={cell.row.wasteLines?.items ?? []}
              totalCount={cell.row.wasteLines?.count}
              rowId={cell.id}
              clickDisabled={loadingWasteLines}
              viewMoreDisabled
              onClick={() => {
                handleFetchWasteLines(
                  cell.row.displayedManifestId,
                  cell.row.id
                );
              }}
              collapsedLines={5}
              render={(lines) => {
                return (
                  <>
                    {lines?.map((line, idx) => (
                      <TruncateTypography
                        key={`wasteProfileNumber${idx}`}
                        showTooltip
                        variant="body"
                      >
                        {line.vendorProfileNumber}
                      </TruncateTypography>
                    ))}
                  </>
                );
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "managementMethodCode",
      headerName: "Management Method",
      flex: 1,
      minWidth,
      sortable: false,
      topAligned: true,
      headerClassName: "wrapHeader",
      renderCell: (cell) => {
        return (
          <Box height={"100%"}>
            <ExpandableCell
              items={cell.row.wasteLines?.items ?? []}
              totalCount={cell.row.wasteLines?.count}
              rowId={cell.id}
              clickDisabled={loadingWasteLines}
              viewMoreDisabled
              onClick={() => {
                handleFetchWasteLines(
                  cell.row.displayedManifestId,
                  cell.row.id
                );
              }}
              collapsedLines={5}
              render={(lines) => {
                return (
                  <>
                    {lines?.map((line, idx) => (
                      <TruncateTypography
                        key={`managementMethodCode${idx}`}
                        showTooltip
                        variant="body"
                      >
                        {line.managementMethodCode}
                      </TruncateTypography>
                    ))}
                  </>
                );
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 0.7,
      topAligned: true,
    },
    {
      field: "eManifest",
      headerName: "e‑Manifest",
      flex: 0.7,
      sortable: true,
      align: "center",
      filterKeyType: "boolean",
      renderCell: ({ row }) => {
        if (!row?.hasEManifest) {
          return <></>;
        }

        return <Check />;
      },
    },
    {
      field: "vendorManifest",
      headerName: "Vendor Manifest",
      flex: 0.8,
      sortable: true,
      align: "center",
      headerClassName: "wrapHeader",
      renderCell: ({ row }) => {
        if (!row?.hasVendorManifest) {
          return <></>;
        }

        return <Check />;
      },
    },
  ];

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        persistenceKey={persistenceKey}
        isURLDriven
        columns={columns}
        dataQuery={MANIFESTS_QUERY}
        getItems={(data) => data.wasteManifests.items}
        getCount={(data) => data.wasteManifests.count}
        noDataMessage={`No manifests have been added to this organization.`}
        isRowSelectable={() => false}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
        rowHeight={"auto"}
        commandButtons={getButtons}
        excludeFilterColumns={[
          "wasteLines",
          "wasteProfileNumber",
          "amount",
          "vendorManifest",
        ]}
        columnVisibilityModel={{
          ...(isMobile && {
            epaId: false,
            wasteLines: false,
            amount: false,
            wasteProfileNumber: false,
            managementMethodCode: false,
            vendor: false,
            eManifest: false,
            vendorManifest: false,
          }),
        }}
      />
    </Box>
  );
}

function determineLineDisplay(line: DeepPartial<WasteLine>) {
  if (line?.vendorProfileDescription) {
    return line.vendorProfileDescription;
  }

  return line?.dotHazardous
    ? line.dotPrintedInformation
    : line?.nonHazWasteDescription;
}

function determineAmount(line: DeepPartial<WasteLine>) {
  if (line?.quantity && line.qtyUnitOfMeasureCode) {
    return `${line.quantity} ${getQtyUnits(line.qtyUnitOfMeasureCode)}`;
  }

  if (line?.quantityTons) {
    return `${line.quantityTons} tons`;
  }

  if (line?.quantityKg) {
    return `${line.quantityKg} kg`;
  }
}

function getQtyUnits(code: string) {
  switch (code) {
    case "G":
      return "gals";
    case "K":
      return "kg";
    case "L":
      return "L";
    case "M":
      return "MT";
    case "N":
      return "cm";
    case "P":
      return "lbs";
    case "T":
      return "T";
    case "Y":
      return "cy";
    default:
      return "";
  }
}
